import useTracking from "../../common/tracking/ninja/useTracking";
import { useRecentSearches } from "./useRecentSearches";
import { useSearchResults } from "./useSearchResults";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
/**
 * Checks whether the user has accepted strictly necessary cookies via OneTrust.
 *
 * OneTrust injects `window.OnetrustActiveGroups` as a comma-separated string of
 * consented category IDs. "C0001" is the OneTrust ID for strictly necessary cookies,
 * which is the minimum required before writing any first-party cookie.
 */
function isStrictlyCookiesAllowed() {
    var _a;
    return (((_a = window
        .OnetrustActiveGroups) !== null && _a !== void 0 ? _a : "").indexOf("C0001") !== -1);
}
/**
 * Persists the selected offer variant (body style / natcode) in a short-lived
 * first-party cookie so that the server can pre-select the correct variant on
 * the next page load.
 *
 * Cookie format : `visit_<idaggregate>=<natcode>`
 * Lifetime      : 24 hours (86400 s), path-scoped to the whole site.
 *
 * `idaggregate` identifies the aggregated offer listing and `natcode` identifies
 * the specific body-style/equipment variant within it. Both values are
 * URL-encoded before being written to guard against special characters.
 *
 * The cookie is only written when OneTrust strictly-necessary consent is present
 * (see `isStrictlyCookiesAllowed`), and only when the selected item actually
 * carries variant data (not all search results do).
 */
function setOfferVariantCookie(item) {
    if (item.idaggregate && item.natcode && isStrictlyCookiesAllowed()) {
        document.cookie = `visit_${encodeURIComponent(item.idaggregate)}=${encodeURIComponent(item.natcode)}; path=/; max-age=86400`;
    }
}
export function useSearchOrchestration({ onSelect } = {}) {
    const { trackEvent } = useTracking();
    const [query, setQuery] = useState("");
    const { loading, error, groups, noResultSuggestions, isTypoFallback } = useSearchResults(query);
    const { recent, clearRecent, addRecent } = useRecentSearches();
    const hasTrackedTypingRef = useRef(false);
    const noResultTrackedQueryRef = useRef(null);
    useEffect(() => {
        if (!query || loading || error)
            return;
        const hasResults = groups.some((g) => g.items.length > 0);
        if (!hasResults && noResultTrackedQueryRef.current !== query) {
            noResultTrackedQueryRef.current = query;
            trackEvent("search_text_no_result", { keyword: query });
        }
    }, [groups]);
    const trackTypingOnce = useCallback((value) => {
        if (!hasTrackedTypingRef.current && value.length > 0) {
            hasTrackedTypingRef.current = true;
            trackEvent("search_text_type", { keyword: value });
        }
    }, [trackEvent]);
    const handleClearRecent = useCallback(() => {
        trackEvent("search_text_clear");
        clearRecent();
    }, [trackEvent, clearRecent]);
    const handleSelectSuggestion = useCallback((item) => {
        trackEvent("suggested_keyword_search", { keyword: item.name });
        addRecent(item);
        setOfferVariantCookie(item);
        onSelect === null || onSelect === void 0 ? void 0 : onSelect();
        window.location.href = item.url;
    }, [trackEvent, addRecent, onSelect]);
    const handleSelectRecentItem = useCallback((item) => {
        trackEvent("suggested_keyword_search", { keyword: item.name });
        if (!("savedAt" in item)) {
            addRecent(item);
            setOfferVariantCookie(item);
        }
        onSelect === null || onSelect === void 0 ? void 0 : onSelect();
        window.location.href = item.url;
    }, [trackEvent, addRecent, onSelect]);
    return useMemo(() => ({
        query,
        setQuery,
        trackTypingOnce,
        loading,
        error,
        groups,
        noResultSuggestions,
        isTypoFallback,
        recent,
        handleClearRecent,
        handleSelectSuggestion,
        handleSelectRecentItem,
    }), [
        query,
        loading,
        error,
        groups,
        noResultSuggestions,
        isTypoFallback,
        recent,
        trackTypingOnce,
        handleClearRecent,
        handleSelectSuggestion,
        handleSelectRecentItem,
    ]);
}
